import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChartBarIcon,
  CheckIcon,
  SunIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { useContext } from "react";
import { AuthContext } from "../AuthContext";
import { differenceInDays, format, parseISO, isValid } from 'date-fns';
import HolidayChart from "./holidaychart";
import AvailabilityGraph from "./holidaygraph";
import { CloseButton } from "@headlessui/react";
import { differenceInCalendarDays } from 'date-fns';
import HolidayCal from "./holidaycal";
import HolidayTeam from "./holidayteammember";

export default function HolidayApprove() {
  const [holiday, setHoliday] = useState([]);
  const [staff, setStaff] = useState([]);
  const [request, setRequest] = useState(false);
  const [policy, setPolicy] = useState([]);
  const [avail, setAvail] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [finishTime, setFinishTime] = useState('');
  const [success, setSuccess] = useState(null);
  const [team, setTeam] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [openInput, setOpenInput] = useState(null);
  const [graph, setGraph] = useState(false);

  // New filter states
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [staffNameFilter, setStaffNameFilter] = useState('');

  // Document upload state
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const user = useContext(AuthContext);
  const token = localStorage.getItem('token');

  // --- Fetch functions (unchanged) ---
  const fetchData = useCallback(() => {
    const fetchStaff = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/staff', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch staff');

        const response2 = await fetch('https://api.insightos.co.uk/api/policies', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response2.ok) throw new Error('Failed to fetch policies');

        const data = await response.json();
        const data2 = await response2.json();
        const policyData = data2.data;
        setPolicy(policyData);

        const filterStaff =
          user.access === 'admin'
            ? data.data
            : data.data.filter((staff) => staff.Department === user.department);

        setStaff(filterStaff);
      } catch (error) {
        console.error('Error fetching staff:', error);
      }
    };
    fetchStaff();
  }, [token, user.access, user.id, user.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchHoliday = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/holiday', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setHoliday(data.data);
      } catch (error) {
        console.error('Failed to fetch holiday:', error);
      }
    };
    fetchHoliday();
  }, [staff]);

  // --- Handlers (unchanged) ---
  const handleApprove = async (person) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/approve/holiday/${person.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          Holiday: 1,
          FromDate: person.FromDate,
          ToDate: person.ToDate,
          Name: person.Name,
          StaffId: person.StaffId,
          Hours: person.Hours,
          DaysOpen: policy[0]?.DaysOpen,
        }),
      });
      if (!response.ok) throw new Error('Failed to approve holiday');
      setHoliday(holiday.map(h => h.id === person.id ? { ...h, Approved: 1 } : h));
    } catch (error) {
      console.error('Error approving holiday:', error);
    }
  };

  const handleReject = async (person) => {
    console.log('Reject holiday:', person);
    // Implement if needed
  };

  const handleHalfDay = async (person) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/approve/halfholiday/${person.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          Holiday: 1,
          FromDate: person.FromDate,
          ToDate: person.ToDate,
          Name: person.Name,
          StaffId: person.StaffId,
          StartTime: startTime,
          FinishTime: finishTime,
          Hours: person.Hours,
        }),
      });
      if (!response.ok) throw new Error('Failed to approve half day holiday');
      setOpenInput(null);
      setSuccess('Half day holiday approved successfully');
      setHoliday(holiday.map(h => h.id === person.id ? { ...h, Approved: 1 } : h));
    } catch (error) {
      console.error('Error approving half day holiday:', error);
    }
  };

  const handleDelete = async (person) => {
    if (isDeleteLocked(person)) {
      setSuccess('This holiday cannot be deleted because it has already started or completed.');
      return;
    }
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/deleteholiday/${person.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ holiday: person }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to delete holiday');
      }
      setHoliday(holiday.filter(h => h.id !== person.id));
      setSuccess('Holiday deleted successfully.');
    } catch (error) {
      console.error('Error deleting holiday:', error);
      setSuccess(error?.message || 'Failed to delete holiday.');
    }
  };

  // --- Helper functions (unchanged) ---
  const formatDateDisplay = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const dateOnlyOrNull = (value) => {
    const parsed = parseISO(String(value || ''));
    if (!isValid(parsed)) return null;
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  };

  const isDeleteLocked = (person) => {
    const fromDate = dateOnlyOrNull(person?.FromDate);
    if (!fromDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= fromDate;
  };

  // --- Document upload handler (unchanged) ---
  const handleDocUpload = async () => {
    if (!uploadFile) {
      setUploadMessage('Please select a file.');
      return;
    }
    if (!selectedEmployeeId) {
      setUploadMessage('Please select an employee.');
      return;
    }

    const formData = new FormData();
    formData.append('document', uploadFile);
    formData.append('employeeId', selectedEmployeeId);

    setUploading(true);
    setUploadMessage('');

    try {
      const response = await fetch('https://api.insightos.co.uk/api/holiday-doc-scan', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setUploadMessage('✅ Document validated and uploaded successfully!');
        setShowDocUpload(false);
        fetchData();
      } else {
        setUploadMessage(`❌ ${data.error || 'Validation failed'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadMessage('❌ Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // --- NEW: Compute filtered and sorted holiday list ---
  const filteredAndSortedHoliday = useMemo(() => {
    let filtered = [...holiday];

    // Apply date range filter (FromDate >= startDateFilter and ToDate <= endDateFilter)
    if (startDateFilter) {
      filtered = filtered.filter(h => h.FromDate && h.FromDate >= startDateFilter);
    }
    if (endDateFilter) {
      filtered = filtered.filter(h => h.ToDate && h.ToDate <= endDateFilter);
    }

    // Apply staff name filter
    if (staffNameFilter) {
      filtered = filtered.filter(h => h.Name.toLowerCase().includes(staffNameFilter.toLowerCase()));
    }

    // Sort: pending first (Approved !== 1), then by FromDate ascending (oldest first)
    filtered.sort((a, b) => {
      const aPending = a.Approved !== 1;
      const bPending = b.Approved !== 1;
      if (aPending !== bPending) return aPending ? -1 : 1;
      const aDate = new Date(a.FromDate).getTime();
      const bDate = new Date(b.FromDate).getTime();
      return aDate - bDate;
    });

    return filtered;
  }, [holiday, startDateFilter, endDateFilter, staffNameFilter]);

  // --- Render ---
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header Section (unchanged) */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <CalendarDaysIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Holiday Management</h1>
                <p className="text-sm text-gray-500">Manage and approve holiday requests</p>
              </div>
            </div>

            <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
              <button
                onClick={() => setRequest(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                <SunIcon className="h-5 w-5 mr-2" />
                My Request
              </button>
              <button
                onClick={() => setTeam(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-orange-600 to-orange-700 hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
              >
                <SunIcon className="h-5 w-5 mr-2" />
                Team Request
              </button>

              {!avail ? (
                <button
                  onClick={() => setAvail(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                >
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  View Availability
                </button>
              ) : (
                <button
                  onClick={() => setAvail(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  Close Availability
                </button>
              )}

              {!graph ? (
                <button
                  onClick={() => setGraph(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                >
                  <ChartBarIcon className="h-5 w-5 mr-2" />
                  Analytics
                </button>
              ) : (
                <button
                  onClick={() => setGraph(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  Close Analytics
                </button>
              )}

              <button
                onClick={() => {
                  setShowDocUpload(true);
                  setUploadMessage('');
                  setUploadFile(null);
                  setSelectedEmployeeId(user.id || '');
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
              >
                <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                Upload Holiday Document
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {avail && (
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <UserGroupIcon className="h-6 w-6 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Team Availability</h3>
              </div>
              <HolidayChart />
            </div>
          </div>
        )}

        {graph && (
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <ChartBarIcon className="h-6 w-6 text-purple-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Holiday Analytics</h3>
              </div>
              <AvailabilityGraph />
            </div>
          </div>
        )}

        {request && (
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <SunIcon className="h-6 w-6 text-white mr-3" />
                    <h3 className="text-lg font-semibold text-white">Holiday Request Form</h3>
                  </div>
                  <button onClick={() => setRequest(false)} className="text-white hover:text-gray-200">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <HolidayCal onTriggerParentEffect={fetchData} holiday={holiday} />
              </div>
            </div>
          </div>
        )}

        {team && (
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <SunIcon className="h-6 w-6 text-white mr-3" />
                    <h3 className="text-lg font-semibold text-white">Holiday Request Form</h3>
                  </div>
                  <button onClick={() => setTeam(false)} className="text-white hover:text-gray-200">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <HolidayTeam onTriggerParentEffect={fetchData} holiday={holiday} />
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-lg bg-green-50 p-4 border border-green-200">
            <div className="flex">
              <CheckIcon className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Policy dates banner */}
        {policy.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center text-blue-800">
              <CalendarDaysIcon className="h-5 w-5 mr-2" />
              <span className="font-medium">
                Holiday Policy Period: {format(parseISO(policy[0].HolidayStart), 'dd/MM/yyyy')} –{' '}
                {format(parseISO(policy[0].HolidayEnd), 'dd/MM/yyyy')}
              </span>
            </div>
          </div>
        )}

        {/* NEW: Filters */}
        <div className="mb-6 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Staff Name</label>
            <select
              value={staffNameFilter}
              onChange={(e) => setStaffNameFilter(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">All Staff</option>
              {[...new Set(holiday.map(h => h.Name))].sort().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Holiday Requests Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <CalendarDaysIcon className="h-6 w-6 text-gray-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Holiday Requests</h3>
              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {filteredAndSortedHoliday.length} requests
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Staff Member</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider hidden sm:table-cell">Department</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">From Date</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">To Date</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Time Period</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider hidden sm:table-cell">Duration</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider hidden md:table-cell">Reason</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedHoliday.map((person) => {
                  const staffMember = staff.find((s) => s.id === person.StaffId) || {};
                  const deleteLocked = isDeleteLocked(person);
                  const holidayTakenSwitch = Number(person?.HolidayTakenSwitch) === 1;
                  const lockText = holidayTakenSwitch
                    ? 'Locked: Holiday already completed'
                    : 'Locked: Holiday has started';
                  const isPending = person.Approved !== 1;

                  return (
                    <tr
                      key={person.id}
                      className={`${isPending ? 'animate-pulse' : ''} hover:bg-gray-50 transition-colors duration-200`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
                              <span className="text-xs font-medium text-white">
                                {person.Name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{person.Name}</div>
                            <div className="text-sm text-gray-500">{person.Role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {staffMember.Department || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatDateDisplay(person.FromDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatDateDisplay(person.ToDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {person.FullDay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                          {person.Hours} hrs
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell max-w-xs truncate">
                        {person.Notes || 'No reason provided'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {person.Approved === 1 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckIcon className="h-3 w-3 mr-1" />
                              Approved - {person.ApprovedBy}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              Pending
                            </span>
                          )}
                          {holidayTakenSwitch && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              <CheckIcon className="h-3 w-3 mr-1" />
                              Holiday Taken
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-normal text-sm font-medium space-y-5 min-w-[280px] align-top">
                        <div className="flex flex-col space-y-2">
                          {person.Approved === 0 && person.FullDay !== "Half" && (
                            <>
                              <button
                                data-time-saved="10"
                                data-component="holiday-approve-approve-button"
                                data-feature="approve_holiday_put"
                                onClick={() => handleApprove(person)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                              >
                                <CheckIcon className="h-3 w-3 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleReject(person)}
                                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                              >
                                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Reject
                              </button>
                            </>
                          )}

                          {(user.access === 'admin' || (user.access === 'manager' && user.department === staffMember.Department)) && (
                            !deleteLocked ? (
                              <button
                                onClick={() => handleDelete(person)}
                                title="Delete holiday request"
                                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                              >
                                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            ) : (
                              <span
                                title={lockText}
                                className="inline-flex items-center px-3 py-1.5 border border-indigo-200 text-xs font-medium rounded-md text-indigo-700 bg-indigo-50"
                              >
                                <CheckIcon className="h-3 w-3 mr-1" />
                                Taken
                              </span>
                            )
                          )}
                        </div>

                        {person.FullDay === "Half" && person.Approved === 0 && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            Requested: {person.Start}
                            <div className="space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Shift Start Time</label>
                                  <input
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Shift End Time</label>
                                  <input
                                    type="time"
                                    value={finishTime}
                                    onChange={(e) => setFinishTime(e.target.value)}
                                    className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                              <button
                                data-component="holiday-approve-halfday-button"
                                data-feature="approve_halfday_holiday_put"
                                data-time-saved="10"
                                onClick={() => handleHalfDay(person)}
                                className="w-full inline-flex justify-center items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                              >
                                <CheckIcon className="h-3 w-3 mr-1" />
                                Confirm Half Day
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Document Upload Modal (unchanged) */}
      {showDocUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <DocumentArrowUpIcon className="h-6 w-6 text-indigo-600 mr-2" />
                Upload Holiday Document
              </h3>
              <button
                onClick={() => {
                  setShowDocUpload(false);
                  setUploadMessage('');
                  setUploadFile(null);
                  setSelectedEmployeeId('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Employee selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">-- Choose an employee --</option>
                  {staff.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.Name} (ID: {emp.EmployeeID || emp.id})
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected employee info */}
              {selectedEmployeeId && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Employee ID:</span>{' '}
                    {staff.find(e => e.id === Number(selectedEmployeeId))?.EmployeeID || selectedEmployeeId}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Name:</span>{' '}
                    {staff.find(e => e.id === Number(selectedEmployeeId))?.Name || ''}
                  </p>
                </div>
              )}

              {/* Policy dates */}
              {policy.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Holiday Policy:</span>{' '}
                    {format(parseISO(policy[0].HolidayStart), 'dd/MM/yyyy')} –{' '}
                    {format(parseISO(policy[0].HolidayEnd), 'dd/MM/yyyy')}
                  </p>
                </div>
              )}
              <div className="text-sm text-gray-500">
                <p className="font-medium">Note:</p>
                <p className="mt-1">Please ensure the uploaded document is a valid PDF and adheres to the holiday policy dates. It needs to show the hours remaining and hours taken, between the holiday start and end dates. The EmployeeID and Name must be included. This is so our AI can accurately process the information.</p>
              </div>

              {/* File input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Document</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Accepted: PDF</p>
              </div>

              {/* Upload message */}
              {uploadMessage && (
                <div className={`text-sm p-2 rounded ${uploadMessage.startsWith('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {uploadMessage}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={() => {
                    setShowDocUpload(false);
                    setUploadMessage('');
                    setUploadFile(null);
                    setSelectedEmployeeId('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDocUpload}
                  disabled={!uploadFile || !selectedEmployeeId || uploading}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all ${
                    !uploadFile || !selectedEmployeeId || uploading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800'
                  }`}
                >
                  {uploading ? 'Uploading...' : 'Submit Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}